import React, { useState } from 'react';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import useBundleTranslation from 'i18n';
import { useParams } from 'react-router-dom';
import { Params as UrlParams } from 'react-router';
import { formAPI } from 'api/form';
import { FormControlButtonProps, FormElementControlPropsType } from './index';
import { processSettingsUrl } from 'components/common/form/formTools';
import { useFormState } from 'react-hook-form';
import IconMi from 'components/common/icon/IconMi';
import AsyncPopup from 'components/common/popup/AsyncPopup';
import FormComponent from 'components/common/form/Form';
import { FormComponentBuilder } from 'components/common/form/layout';
import { useMiNavigation } from 'hooks/useNavigation';

const FormButton = ({ controlProps, elementProps }: FormElementControlPropsType<FormControlButtonProps>) => {
    const { t } = useBundleTranslation(elementProps?.translationNS ?? 'components/common/form/form');
    const urlParams: UrlParams = useParams();
    const [isLoading, toggleLoading] = useState(false);
    const url = processSettingsUrl(controlProps.url, Object.keys(urlParams), urlParams);
    const icon = controlProps.icon ? <IconMi icon={controlProps.icon} /> : null;
    const tooltip = controlProps.tooltip ? t(controlProps.tooltip as string) : null;

    const [showPopup, setShowPopup] = useState(false);

    const { isDirty } = useFormState({ control: controlProps.form.hookFormControl });
    const navigate = useMiNavigation();
    const handleClick = async () => {
        if (controlProps.action === 'redirect') {
            if (isDirty && controlProps.save) {
                controlProps.form.formSave().then((response) => {
                    if (response.data.status == 'OK') {
                        setTimeout(() => navigate(url), 100);
                    }
                });
                return;
            }
            navigate(url);
            return;
        }

        if (controlProps.action === 'popup') {
            setShowPopup(true);
            return;
        }

        try {
            toggleLoading(true);
            await formAPI.save(url);
        } finally {
            toggleLoading(false);
        }
    };

    return (
        <>
            <Tooltip placement={'top-start'} title={tooltip}>
                <Button
                    data-test={controlProps.uid}
                    onClick={handleClick}
                    variant={controlProps.variant}
                    startIcon={isLoading ? null : icon}
                    disabled={controlProps.disabled || isLoading}
                >
                    {isLoading && <CircularProgress sx={{ marginRight: '5px' }} color="inherit" size={12} />}
                    {isDirty && controlProps.save ? t('save') + ' & ' : ''} {t(controlProps.label as string)}
                </Button>
            </Tooltip>
            {showPopup && (
                <>
                    {controlProps.popupConfig?.popupType === 'form' ? (
                        <FormComponent
                            isPopup
                            popupType={controlProps.popupConfig?.popupMode ?? 'new'}
                            pk={url}
                            uid={controlProps.uid ?? url}
                            settingsUrl={url}
                            onHide={() => {
                                setShowPopup(false);
                            }}
                            popupSettings={controlProps.popupConfig}
                            afterSave={(response: any) => {
                                if (response.data?.redirectUrl) {
                                    window.location.href = response.data.redirectUrl;
                                }
                                setShowPopup(false);
                            }}
                        />
                    ) : (
                        <AsyncPopup
                            popupSettings={controlProps.popupConfig ?? {}}
                            settingsUrl={controlProps.url}
                            onConfirm={() => {
                                setShowPopup(false);
                            }}
                            onHide={() => {
                                setShowPopup(false);
                            }}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default FormButton;

export class FormButtonBuilder extends FormComponentBuilder {
    prepareProps(): FormControlButtonProps {
        return {
            ...this.controlProps,
            urlParams: {
                ...this.elementProps.urlParams,
                ...this.elementProps.componentProps?.urlParams,
            } as UrlParams,
            url: this.elementProps.componentProps?.url ?? '',
            icon: this.elementProps.componentProps?.icon ?? undefined,
            disabled: this.elementProps.componentProps?.disabled ?? false,
            save: this.elementProps.componentProps?.save ?? false,
            variant: this.elementProps.componentProps?.variant ?? 'outlined',
            checkOnDirtyField: this.elementProps.componentProps?.checkOnDirtyField ?? '',
            action: this.elementProps.componentProps?.action ?? '',
            popupConfig: this.elementProps.componentProps?.popupConfig ?? {},
        };
    }
}
